import type { ModelData, ScenarioResult } from '@/types/models';
import { formatCurrency } from '@/utils/formatters';

interface ScenarioTabProps {
  data: ModelData;
}

export function ScenarioTab({ data }: ScenarioTabProps) {
  const scenarios = data?.scenarios;
  const revenue = data?.revenue || [];
  const pnl = data?.pnl || [];

  // Calculate base scenario from actual data
  const year5Revenue = revenue[revenue.length - 1]?.total || 0;
  const year5Ebitda = pnl[pnl.length - 1]?.ebitda || 0;
  const year5NetIncome = pnl[pnl.length - 1]?.netIncome || 0;
  const year1Revenue = revenue[0]?.total || 1;
  const cagr = revenue.length > 1 
    ? (Math.pow(year5Revenue / year1Revenue, 1 / (revenue.length - 1)) - 1) * 100
    : 0;

  // If no scenario data, generate estimated scenarios
  const baseScenario: ScenarioResult = scenarios?.base || {
    year5Revenue,
    year5Ebitda,
    year5NetIncome,
    cagr,
  };

  const conservativeScenario: ScenarioResult = scenarios?.conservative || {
    year5Revenue: year5Revenue * 0.75,
    year5Ebitda: year5Ebitda * 0.6,
    year5NetIncome: year5NetIncome * 0.5,
    cagr: cagr * 0.7,
  };

  const optimisticScenario: ScenarioResult = scenarios?.optimistic || {
    year5Revenue: year5Revenue * 1.3,
    year5Ebitda: year5Ebitda * 1.5,
    year5NetIncome: year5NetIncome * 1.8,
    cagr: cagr * 1.25,
  };

  const metrics = [
    { key: 'year5Revenue', label: 'Year 5 Revenue', format: 'currency' },
    { key: 'year5Ebitda', label: 'Year 5 EBITDA', format: 'currency' },
    { key: 'year5NetIncome', label: 'Year 5 Net Income', format: 'currency' },
    { key: 'cagr', label: 'Revenue CAGR', format: 'percentage' },
  ] as const;

  return (
    <div className="space-y-8">
      {/* Scenario Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Conservative */}
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border border-orange-200 overflow-hidden">
          <div className="bg-orange-200/50 px-4 py-3 border-b border-orange-200">
            <h3 className="font-semibold text-orange-900">🛡️ Conservative Case</h3>
            <p className="text-sm text-orange-700">Lower growth assumptions</p>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <div className="text-sm text-orange-600">Year 5 Revenue</div>
              <div className="text-xl font-bold text-orange-900">
                {formatCurrency(conservativeScenario.year5Revenue)}
              </div>
            </div>
            <div>
              <div className="text-sm text-orange-600">Year 5 EBITDA</div>
              <div className={`text-lg font-semibold ${conservativeScenario.year5Ebitda >= 0 ? 'text-orange-900' : 'text-red-600'}`}>
                {formatCurrency(conservativeScenario.year5Ebitda)}
              </div>
            </div>
            <div>
              <div className="text-sm text-orange-600">CAGR</div>
              <div className="text-lg font-semibold text-orange-900">
                {conservativeScenario.cagr?.toFixed(1) || 0}%
              </div>
            </div>
          </div>
        </div>

        {/* Base Case */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200 overflow-hidden ring-2 ring-blue-400">
          <div className="bg-blue-200/50 px-4 py-3 border-b border-blue-200">
            <h3 className="font-semibold text-blue-900">📊 Base Case</h3>
            <p className="text-sm text-blue-700">Current assumptions</p>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <div className="text-sm text-blue-600">Year 5 Revenue</div>
              <div className="text-xl font-bold text-blue-900">
                {formatCurrency(baseScenario.year5Revenue)}
              </div>
            </div>
            <div>
              <div className="text-sm text-blue-600">Year 5 EBITDA</div>
              <div className={`text-lg font-semibold ${baseScenario.year5Ebitda >= 0 ? 'text-blue-900' : 'text-red-600'}`}>
                {formatCurrency(baseScenario.year5Ebitda)}
              </div>
            </div>
            <div>
              <div className="text-sm text-blue-600">CAGR</div>
              <div className="text-lg font-semibold text-blue-900">
                {baseScenario.cagr?.toFixed(1) || cagr.toFixed(1)}%
              </div>
            </div>
          </div>
        </div>

        {/* Optimistic */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200 overflow-hidden">
          <div className="bg-green-200/50 px-4 py-3 border-b border-green-200">
            <h3 className="font-semibold text-green-900">🚀 Optimistic Case</h3>
            <p className="text-sm text-green-700">Higher growth assumptions</p>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <div className="text-sm text-green-600">Year 5 Revenue</div>
              <div className="text-xl font-bold text-green-900">
                {formatCurrency(optimisticScenario.year5Revenue)}
              </div>
            </div>
            <div>
              <div className="text-sm text-green-600">Year 5 EBITDA</div>
              <div className={`text-lg font-semibold ${optimisticScenario.year5Ebitda >= 0 ? 'text-green-900' : 'text-red-600'}`}>
                {formatCurrency(optimisticScenario.year5Ebitda)}
              </div>
            </div>
            <div>
              <div className="text-sm text-green-600">CAGR</div>
              <div className="text-lg font-semibold text-green-900">
                {optimisticScenario.cagr?.toFixed(1) || 0}%
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">📋 Scenario Comparison</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 px-4 font-semibold text-slate-700 bg-slate-50">
                  Metric
                </th>
                <th className="text-right py-3 px-4 font-semibold text-orange-700 bg-orange-50">
                  Conservative
                </th>
                <th className="text-right py-3 px-4 font-semibold text-blue-700 bg-blue-50">
                  Base Case
                </th>
                <th className="text-right py-3 px-4 font-semibold text-green-700 bg-green-50">
                  Optimistic
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {metrics.map((metric) => (
                <tr key={metric.key} className="hover:bg-slate-50">
                  <td className="py-3 px-4 font-medium text-slate-700">
                    {metric.label}
                  </td>
                  <td className="py-3 px-4 text-right text-slate-700">
                    {metric.format === 'currency' 
                      ? formatCurrency(conservativeScenario[metric.key] || 0)
                      : `${(conservativeScenario[metric.key] || 0).toFixed(1)}%`
                    }
                  </td>
                  <td className="py-3 px-4 text-right font-semibold text-slate-900">
                    {metric.format === 'currency' 
                      ? formatCurrency(baseScenario[metric.key] || 0)
                      : `${(baseScenario[metric.key] || cagr).toFixed(1)}%`
                    }
                  </td>
                  <td className="py-3 px-4 text-right text-slate-700">
                    {metric.format === 'currency' 
                      ? formatCurrency(optimisticScenario[metric.key] || 0)
                      : `${(optimisticScenario[metric.key] || 0).toFixed(1)}%`
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assumptions Note */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex gap-3">
          <span className="text-2xl">💡</span>
          <div>
            <h4 className="font-semibold text-amber-900 mb-1">Scenario Assumptions</h4>
            <p className="text-sm text-amber-800">
              <strong>Conservative:</strong> Assumes 25% lower growth rates and higher churn<br />
              <strong>Base Case:</strong> Uses your current input assumptions<br />
              <strong>Optimistic:</strong> Assumes 30% higher growth rates and improved retention
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
